// ============================================================================
// Presentation views for KPI Tracker Full Screen mode (2026-08-11).
// Triggered ONLY when the Full Screen toggle is on. Sizing is scoreboard-scale:
// large enough to read a revenue figure and ON TARGET / OFF TRACK pill from
// 25 feet away on an 80" TV. Desk-view layouts are unchanged in each mode.
//
// 2026-08-12 additions per Nathanael's oversight-restore call: Meeting
// Attendance section, Next Week goal editing (real TV-side interaction —
// same computer drives the TV, no laptop mirror), and Scorecard KPI
// checklist (all 10 items per tech) all restored at scoreboard scale.
// ============================================================================
import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Trophy, Check, X as XIcon } from "lucide-react";

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt$c = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtCents = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// CC-023: click a tech's revenue to list the completed jobs behind it. No portal (works in fullscreen);
// position: fixed so overflow-x-auto table wrappers don't clip it.
const PANEL_W = 320, PANEL_H = 360;
function panelPos(el) {
  const r = el.getBoundingClientRect();
  const top = r.bottom + 8 + PANEL_H > window.innerHeight ? Math.max(8, r.top - 8 - PANEL_H) : r.bottom + 8;
  return { top, left: Math.max(8, Math.min(r.right - PANEL_W, window.innerWidth - PANEL_W - 8)) };
}

export function RevenueDrilldown({ techName, revenue, jobs, className, testIdPrefix }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const open = pos != null;
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setPos(null); };
    const onMove = (e) => { if (!(ref.current && e.target instanceof Node && ref.current.contains(e.target))) setPos(null); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);
  const rows = jobs || [];
  const sumCents = rows.reduce((s, r) => s + Math.round(Number(r.revenue || 0) * 100), 0);
  return (
    <span className="inline-block" ref={ref}>
      <button type="button" onClick={() => setPos(open ? null : panelPos(ref.current))} className={`${className} underline decoration-dotted underline-offset-4 hover:opacity-80 transition-opacity`}
              title="Show the completed jobs behind this total" data-testid={`${testIdPrefix}-revenue-${techName}`}>
        {fmtCents(revenue)}
      </button>
      {open && (
        <div className="fixed z-50 w-[320px] max-h-[360px] overflow-auto bg-white border border-[#e7e9ee] rounded-lg shadow-xl text-left"
             style={pos}
             data-testid={`${testIdPrefix}-revenue-jobs-${techName}`}>
          <div className="px-3 py-2 text-[11px] uppercase tracking-widest font-bold text-[color:var(--cc-ink-muted)] border-b border-[#eef1f6]">
            {techName} · completed jobs
          </div>
          {rows.length === 0 ? (
            <div className="px-3 py-3 text-[13px] text-[color:var(--cc-ink-muted)]">No completed jobs this week.</div>
          ) : (
            <table className="w-full text-[13px] font-mono-data">
              <tbody>
                {rows.map((r) => (
                  <tr key={r.job_number} className="border-b border-[#f3f5f8]" data-testid={`${testIdPrefix}-revenue-job-${techName}-${r.job_number}`}>
                    <td className="px-3 py-1.5 text-[#0e1420]">#{r.job_number}</td>
                    <td className="px-3 py-1.5 text-right text-[#0e1420]">{fmtCents(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="px-3 py-2 flex justify-between text-[13px] font-mono-data font-bold bg-[#fafbfc]" data-testid={`${testIdPrefix}-revenue-sum-${techName}`}>
            <span>{rows.length} jobs</span><span>{fmtCents(sumCents / 100)}</span>
          </div>
        </div>
      )}
    </span>
  );
}

const STATUS_PILL = {
  ON_TARGET: { bg: "bg-[#0e8a68]", text: "text-white", label: "ON TARGET" },
  WATCH:     { bg: "bg-[#eab308]", text: "text-[#0e1420]", label: "WATCH" },
  OFF_TRACK: { bg: "bg-[#ef5757]", text: "text-white", label: "OFF TRACK" },
  NO_GOAL:   { bg: "bg-[#4b5563]", text: "text-white", label: "NO GOAL" },
};

function BigStatusPill({ status }) {
  const s = STATUS_PILL[status] || STATUS_PILL.NO_GOAL;
  return (
    <span data-testid={`pres-status-${status}`} className={`inline-flex items-center px-6 py-3 rounded-lg text-[26px] font-mono-data font-bold tracking-wider ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

// TV-side editable Next Week goal. Same computer drives the TV, so we need
// real click targets and clear commit/cancel — not a squinty desk input.
// Read state: shows the current next-week goal at scoreboard scale + a
// "SET GOAL" tap target. Edit state: 56px numeric input, step ±$500 buttons,
// giant Save/Cancel. Enter commits, Escape cancels.
function NextWeekGoalCell({ techName, current, nextWeekOf, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(current != null ? String(current) : "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const startEdit = () => { setVal(current != null ? String(current) : ""); setErr(null); setEditing(true); };
  const cancel = () => { setEditing(false); setErr(null); };
  const bump = (delta) => {
    const n = Number(val) || 0;
    setVal(String(Math.max(0, Math.round(n + delta))));
  };
  const commit = async () => {
    const num = Number(val);
    if (val === "" || Number.isNaN(num) || num < 0) { setErr("Enter a positive number"); return; }
    setSaving(true); setErr(null);
    try {
      await api.patch("/tech-goals", { tech_name: techName, week_of: nextWeekOf, weekly_goal: num });
      onSaved?.();
      setEditing(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <button type="button" onClick={startEdit} data-testid={`pres-next-goal-${techName}`}
        className="text-left group focus:outline-none focus:ring-4 focus:ring-[#0072bc]/30 rounded-lg px-3 py-2">
        <div className="text-[14px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Next Week</div>
        {current != null ? (
          <div className="font-mono-data font-bold text-[38px] text-[#0e1420] leading-none mt-1 group-hover:text-[#0072bc]">
            {fmt$c(current)}
          </div>
        ) : (
          <div className="font-mono-data font-bold text-[22px] text-[color:var(--cc-ink-muted)] uppercase tracking-widest border-2 border-dashed border-[#c9cfd8] rounded-md px-3 py-1.5 mt-1 group-hover:border-[#0072bc] group-hover:text-[#0072bc]">
            Set Goal
          </div>
        )}
      </button>
    );
  }

  return (
    <div data-testid={`pres-next-goal-editing-${techName}`} className="border-4 border-[#0072bc] rounded-lg p-3 bg-[#f0f7fb]">
      <div className="text-[14px] uppercase tracking-widest text-[#0072bc] font-bold mb-2">Editing · {techName}</div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => bump(-500)} className="w-14 h-14 text-[28px] font-bold rounded-lg bg-white border-2 border-[#0e1420] text-[#0e1420] hover:bg-[#eef1f6]" data-testid={`pres-next-goal-minus-${techName}`}>−</button>
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[28px] font-mono-data font-bold text-[#0e1420]">$</span>
          <input type="number" min="0" step="100" value={val} autoFocus onFocus={(e) => e.target.select()}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") cancel(); }}
            data-testid={`pres-next-goal-input-${techName}`}
            className="w-full pl-10 pr-3 py-3 text-[38px] font-mono-data font-bold text-right border-2 border-[#0072bc] rounded-lg bg-white focus:outline-none" />
        </div>
        <button type="button" onClick={() => bump(500)} className="w-14 h-14 text-[28px] font-bold rounded-lg bg-white border-2 border-[#0e1420] text-[#0e1420] hover:bg-[#eef1f6]" data-testid={`pres-next-goal-plus-${techName}`}>+</button>
        <button type="button" onClick={commit} disabled={saving}
          data-testid={`pres-next-goal-save-${techName}`}
          className="w-14 h-14 rounded-lg bg-[#0e8a68] text-white hover:bg-[#0a6f52] disabled:opacity-60 flex items-center justify-center">
          <Check size={26} strokeWidth={3} />
        </button>
        <button type="button" onClick={cancel} disabled={saving}
          data-testid={`pres-next-goal-cancel-${techName}`}
          className="w-14 h-14 rounded-lg bg-white border-2 border-[#0e1420] text-[#0e1420] hover:bg-[#eef1f6] flex items-center justify-center">
          <XIcon size={26} strokeWidth={3} />
        </button>
      </div>
      {err && <div className="text-[14px] text-[#991b1b] font-semibold mt-2">{err}</div>}
    </div>
  );
}

// Meeting attendance strip — one big pill per tech, click cycles the same
// Not marked → Present → Absent → Not marked chain as the desk view.
function AttendanceStrip({ attendance, weekOf, onSaved }) {
  if (!attendance || !attendance.attendees || attendance.attendees.length === 0) return null;
  return (
    <section data-testid="pres-attendance" className="bg-white border border-[#e7e9ee] rounded-xl p-6">
      <div className="flex items-baseline gap-4 mb-4">
        <div className="text-[18px] uppercase tracking-[.15em] text-[#0072bc] font-bold">Meeting Attendance</div>
        <div className="text-[16px] font-mono-data text-[color:var(--cc-ink-muted)]">
          {attendance.present} of {attendance.marked ?? attendance.total} marked present
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        {attendance.attendees.map((a) => (
          <BigAttendancePill key={a.tech_name} techName={a.tech_name} present={a.present} weekOf={weekOf} onSaved={onSaved} />
        ))}
      </div>
    </section>
  );
}

function BigAttendancePill({ techName, present, weekOf, onSaved }) {
  const [local, setLocal] = useState(present);
  const [saving, setSaving] = useState(false);
  const cycle = async () => {
    const next = local === null || local === undefined ? true : local === true ? false : null;
    setLocal(next); setSaving(true);
    try {
      await api.patch("/kpi/meeting-attendance", { tech_name: techName, present: next, week_of: weekOf });
      onSaved?.();
    } catch { setLocal(present); }
    finally { setSaving(false); }
  };
  const styles = local === true
    ? "bg-[#0e8a68] text-white border-[#0e8a68]"
    : local === false
    ? "bg-[#ef5757] text-white border-[#ef5757]"
    : "bg-white text-[#4b5563] border-[#c9cfd8] border-dashed";
  const label = local === true ? "PRESENT" : local === false ? "ABSENT" : "NOT MARKED";
  return (
    <button type="button" onClick={cycle} disabled={saving}
      data-testid={`pres-attendance-${techName}`}
      data-attendance-state={local === true ? "present" : local === false ? "absent" : "unmarked"}
      className={`px-6 py-4 rounded-lg border-2 text-[20px] font-mono-data font-bold tracking-wider ${styles} ${saving ? "opacity-60" : ""}`}>
      {techName} · {label}
    </button>
  );
}

// Shared tile for the top-summary strip. Value gets `text-6xl` or larger.
function BigTile({ label, value, sub, accent = "#0072bc", testId }) {
  return (
    <div className="bg-white border-t-8 rounded-xl p-6 flex flex-col justify-between min-h-[180px] shadow-sm" style={{ borderTopColor: accent }} data-testid={testId}>
      <div className="text-[16px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">{label}</div>
      <div className="font-mono-data font-bold text-[64px] leading-none mt-3" style={{ color: accent }}>
        {value}
      </div>
      {sub && <div className="text-[16px] text-[#0e1420] font-semibold mt-2">{sub}</div>}
    </div>
  );
}

// -------- Weekly Wall ------------------------------------------------------
export function WallWeeklyPresentation({ data, onSaved }) {
  const cards = data.techs || [];
  const top = data.top_performer;
  return (
    <div className="space-y-8" data-testid="wall-presentation-weekly">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[18px] uppercase tracking-[.2em] text-[#0072bc] font-bold">Monday Meeting Wall</div>
          <h1 className="font-display font-bold text-[54px] tracking-tight text-[#0e1420] mt-1 leading-none">{data.week_range}</h1>
        </div>
        {top && (
          <div className="text-right">
            <div className="text-[14px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Top Performer</div>
            <div className="font-display font-bold text-[36px] text-[#0e1420] leading-tight">{top.tech_name}</div>
            <div className="font-mono-data font-bold text-[42px] text-[#0072bc] leading-none">{fmt$c(top.revenue)}</div>
          </div>
        )}
      </div>

      {/* Tech rows — huge revenue + status pill, tiny secondary details.
          Now includes an inline Next Week goal editor per row. */}
      <div className="space-y-4">
        {cards.map((c) => {
          const goalPct = c.pct_of_goal;
          return (
            <div key={c.tech_name} data-testid={`pres-row-${c.tech_name}`}
                 className="bg-white border border-[#e7e9ee] rounded-xl px-8 py-6 grid grid-cols-[1.1fr_1.2fr_0.9fr_1.1fr_0.9fr] gap-6 items-center min-h-[128px]">
              <div>
                <div className="font-display font-bold text-[34px] text-[#0e1420] leading-tight">{c.tech_name}</div>
                <div className="text-[14px] text-[color:var(--cc-ink-muted)] font-mono-data uppercase tracking-widest mt-1">{c.unit || "—"}</div>
              </div>
              <div>
                <div className="text-[14px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Revenue</div>
                <RevenueDrilldown techName={c.tech_name} revenue={c.revenue} jobs={c.revenue_jobs} testIdPrefix="pres-wall"
                  className="font-mono-data font-bold text-[52px] text-[#0072bc] leading-none mt-1" />
                <div className="text-[13px] text-[color:var(--cc-ink-muted)] font-mono-data mt-1">{c.job_count || 0} jobs</div>
              </div>
              <div>
                <div className="text-[14px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">This Goal</div>
                <div className="font-mono-data font-bold text-[34px] text-[#0e1420] leading-none mt-1">
                  {c.weekly_goal != null ? fmt$c(c.weekly_goal) : "—"}
                </div>
                <div className="font-mono-data font-bold text-[18px] text-[#0e1420] mt-1">
                  {goalPct != null ? `${goalPct}%` : "—"}
                </div>
              </div>
              <div>
                <NextWeekGoalCell techName={c.tech_name} current={c.next_weekly_goal} nextWeekOf={data.next_week_of} onSaved={onSaved} />
              </div>
              <div className="flex justify-end">
                <BigStatusPill status={c.goal_status} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Meeting Attendance — restored 2026-08-12 */}
      <AttendanceStrip attendance={data.meeting_attendance} weekOf={data.week_of} onSaved={onSaved} />
    </div>
  );
}

// -------- Monthly Wall ----------------------------------------------------
export function WallMonthlyPresentation({ data }) {
  const totals = data.totals || {};
  const techs = data.techs || [];
  const top = techs.find((t) => !t.no_jobs);
  return (
    <div className="space-y-8" data-testid="wall-presentation-monthly">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[18px] uppercase tracking-[.2em] text-[#0072bc] font-bold">Monthly Roll-Up</div>
          <h1 className="font-display font-bold text-[54px] tracking-tight text-[#0e1420] mt-1 leading-none">{data.month_label}</h1>
        </div>
      </div>

      {/* Summary tiles — scoreboard sized */}
      <div className="grid grid-cols-4 gap-5" data-testid="pres-monthly-tiles">
        <BigTile label="Monthly Revenue" value={fmt$c(totals.revenue)} sub={`${totals.job_count || 0} jobs`} accent="#0072bc" testId="pres-monthly-revenue" />
        <BigTile label="Monthly Goal" value={totals.monthly_goal != null ? fmt$c(totals.monthly_goal) : "—"} sub={totals.pct_of_goal != null ? `${totals.pct_of_goal}% of goal` : "no goal set"} accent="#0e1420" />
        <BigTile label="Close Rate" value={totals.close_rate != null ? `${totals.close_rate}%` : "—"} sub={totals.close_rate_weeks > 0 ? `avg across ${totals.close_rate_weeks} week${totals.close_rate_weeks === 1 ? "" : "s"}` : "pending"} accent="#0e8a68" testId="pres-monthly-close-rate" />
        <BigTile label="Weeks Included" value={String((data.weeks_included || []).length)} sub={data.weeks_included && data.weeks_included.length > 0 ? `${data.weeks_included[0]} → ${data.weeks_included[data.weeks_included.length - 1]}` : ""} accent="#4b5563" />
      </div>

      {top && (
        <div className="bg-white border-t-8 rounded-xl p-8 flex items-center gap-8 shadow-sm" style={{ borderTopColor: "#FFC72C" }} data-testid="pres-monthly-top">
          <div className="w-24 h-24 rounded-2xl bg-[#FFC72C] flex items-center justify-center">
            <Trophy size={54} strokeWidth={1.8} className="text-[#0e1420]" />
          </div>
          <div className="flex-1">
            <div className="text-[16px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Top Monthly Revenue</div>
            <div className="font-display font-bold text-[46px] text-[#0e1420] leading-tight">{top.tech_name}</div>
            <div className="text-[16px] text-[color:var(--cc-ink-muted)]">{top.unit}</div>
          </div>
          <div className="text-right">
            <div className="font-mono-data font-bold text-[72px] text-[#0072bc] leading-none">{fmt$c(top.revenue)}</div>
            <div className="text-[16px] font-mono-data text-[color:var(--cc-ink-muted)] mt-1">
              {top.pct_of_goal != null ? `${top.pct_of_goal}% of ${fmt$c(top.monthly_goal)}` : "no goal set"}
            </div>
          </div>
        </div>
      )}

      {/* Tech rows */}
      <div className="space-y-4">
        {techs.map((t) => (
          <div key={t.tech_name} data-testid={`pres-monthly-row-${t.tech_name}`}
               className={`bg-white border border-[#e7e9ee] rounded-xl px-8 py-6 grid grid-cols-[1.3fr_1.4fr_1.1fr_1.2fr] gap-8 items-center min-h-[128px] ${t.no_jobs ? "opacity-60" : ""}`}>
            <div>
              <div className="font-display font-bold text-[36px] text-[#0e1420] leading-tight">{t.tech_name}</div>
              <div className="text-[14px] text-[color:var(--cc-ink-muted)] font-mono-data uppercase tracking-widest mt-1">
                {t.unit || "—"}{t.no_jobs && " · no jobs"}
              </div>
            </div>
            <div>
              <div className="text-[14px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Revenue</div>
              <div className="font-mono-data font-bold text-[60px] text-[#0072bc] leading-none mt-1">{fmt$c(t.revenue)}</div>
              <div className="text-[13px] text-[color:var(--cc-ink-muted)] font-mono-data mt-1">{t.job_count} jobs</div>
            </div>
            <div>
              <div className="text-[14px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Goal</div>
              <div className="font-mono-data font-bold text-[34px] text-[#0e1420] leading-none mt-1">
                {t.monthly_goal != null ? fmt$c(t.monthly_goal) : "—"}
              </div>
              <div className="font-mono-data font-bold text-[22px] text-[#0e1420] mt-1">
                {t.pct_of_goal != null ? `${t.pct_of_goal}%` : "—"}
              </div>
            </div>
            <div className="flex justify-end">
              <BigStatusPill status={t.goal_status} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------- Scorecard ------------------------------------------------------
// Compliance tone thresholds mirror ScorecardMode's complianceTone() so the
// pill color agrees with the desk-view chart.
function complianceBand(pct) {
  if (pct == null) return { bg: "bg-[#4b5563]", text: "text-white", label: "N/A" };
  if (pct >= 75) return { bg: "bg-[#0e8a68]", text: "text-white", label: `${pct}%` };
  if (pct >= 50) return { bg: "bg-[#eab308]", text: "text-[#0e1420]", label: `${pct}%` };
  return { bg: "bg-[#ef5757]", text: "text-white", label: `${pct}%` };
}

// CC-023: mirror the desk badge (ScorecardMode complianceBadgeText) instead of a bare %.
function scorecardBadge(t) {
  if (t.compliance_state === "INCOMPLETE" || (t.required_unanswered ?? 0) > 0) {
    return { bg: "bg-[#fff4d6]", text: "text-[#8a5b00]", label: `INCOMPLETE · ${t.required_unanswered ?? 0} unanswered` };
  }
  if (t.compliance_state === "COMPLIANT") return { bg: "bg-[#0e8a68]", text: "text-white", label: "COMPLIANT · 100%" };
  const band = complianceBand(t.kpi_compliance_pct);
  return { ...band, label: t.kpi_compliance_pct == null ? "KPI N/A" : `KPI ${band.label}` };
}

// KPI checklist item — compact per-item pill at scoreboard scale. Backend
// scorecard aggregates as `pct` per item (share of jobs where the tech got
// credit), so we color by the same complianceBand thresholds as the row
// summary. Room needs to read all 10 items in a glance from 25 ft.
function KpiItemPill({ label, pct, source, unanswered, testId }) {
  const blanks = unanswered || 0;  // CC-219: MANUAL blanks -> INCOMPLETE amber (same as the pill)
  const styles = blanks > 0
    ? "bg-[#fff4d6] text-[#8a5b00] border-[#f3d97a]"
    : pct == null
    ? "bg-white text-[#4b5563] border-[#c9cfd8] border-dashed"
    : pct >= 75
    ? "bg-[#0e8a68] text-white border-[#0e8a68]"
    : pct >= 50
    ? "bg-[#eab308] text-[#0e1420] border-[#eab308]"
    : "bg-[#ef5757] text-white border-[#ef5757]";
  return (
    <div className={`px-3 py-2 rounded-md border-2 ${styles}`} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest opacity-90 font-bold truncate leading-tight" title={label}>{label}</div>
      <div className="flex items-baseline justify-between gap-2 mt-1">
        <span className="text-[22px] font-mono-data font-bold leading-none">
          {pct != null ? `${pct}%` : "—"}{blanks > 0 && <span className="text-[14px]"> · {blanks} blank</span>}
        </span>
        {source && <span className="text-[9px] font-mono-data uppercase tracking-widest opacity-70">{source}</span>}
      </div>
    </div>
  );
}

// CC-206: field close = invoiced / dispatched jobs this week (not agent7's snapshot close_rate).
// CC-242: ServiceTitan close rate (l10_scorecards.tech_performance) is the main close figure.
export const fmtCloseRate = (t) => (t.close_rate_pct == null ? "No ST data" : `${t.close_rate_pct}%`);
export const fmtObp = (t) =>
  t.obp_source === "st_tech_performance"
    ? `$${Number(t.obp_wtd).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : "Not connected";

export const fmtFieldClose = (t) =>
  t.field_close_pct == null ? "No data yet" : `${t.field_close_pct}% (${t.field_close_sold}/${t.field_close_dispatched})`;

export function ScorecardPresentation({ data }) {
  const techs = data.techs || [];
  return (
    <div className="space-y-8" data-testid="scorecard-presentation">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[18px] uppercase tracking-[.2em] text-[#0072bc] font-bold">Weekly Scorecard</div>
          <h1 className="font-display font-bold text-[54px] tracking-tight text-[#0e1420] mt-1 leading-none">{data.week_range}</h1>
        </div>
      </div>

      <div className="space-y-4">
        {techs.map((t) => {
          const comp = scorecardBadge(t);
          const items = t.per_item || [];
          return (
            <div key={t.tech_name} data-testid={`pres-scorecard-row-${t.tech_name}`}
                 className="bg-white border border-[#e7e9ee] rounded-xl px-8 py-6 space-y-5">
              {/* Summary row */}
              <div className="grid grid-cols-[1.3fr_1.4fr_1.0fr_1.1fr_1.1fr] gap-8 items-center min-h-[128px]">
                {/* Tech + unit */}
                <div>
                  <div className="font-display font-bold text-[36px] text-[#0e1420] leading-tight">{t.tech_name}</div>
                  <div className="text-[14px] text-[color:var(--cc-ink-muted)] font-mono-data uppercase tracking-widest mt-1">{t.unit || "—"}</div>
                </div>
                {/* Revenue */}
                <div>
                  <div className="text-[14px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Completed Revenue</div>
                  <div className="font-mono-data font-bold text-[60px] text-[#0072bc] leading-none mt-1">{fmt$c(t.revenue)}</div>
                </div>
                {/* OBP */}
                <div>
                  <div className="text-[14px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">OBP</div>
                  {/* CC-242: OBP from the ST row when present, else "Not connected" */}
                  <div className={t.obp_source === "st_tech_performance"
                         ? "font-mono-data font-bold text-[38px] text-[#0e1420] leading-none mt-1"
                         : "font-mono-data font-bold text-[16px] text-[#4b5563] leading-tight mt-1"}
                       data-testid={`pres-scorecard-obp-${t.tech_name}`}>
                    {fmtObp(t)}
                  </div>
                </div>
                {/* CC-242: ST close rate is primary; CC-206 field close is a small secondary line */}
                <div>
                  <div className="text-[14px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Close rate (ST)</div>
                  <div
                    className={t.close_rate_pct == null
                      ? "font-mono-data font-bold text-[16px] text-[#4b5563] leading-tight mt-1"
                      : "font-mono-data font-bold text-[38px] text-[#0e1420] leading-none mt-1"}
                    data-testid={`pres-scorecard-close-rate-${t.tech_name}`}
                  >
                    {fmtCloseRate(t)}
                  </div>
                  <div className="text-[12px] font-mono-data text-[color:var(--cc-ink-muted)] mt-1" data-testid={`pres-scorecard-field-close-${t.tech_name}`}>
                    Field close (invoiced/dispatched) · {fmtFieldClose(t)}
                  </div>
                </div>
                {/* KPI compliance — the big status pill */}
                <div className="flex justify-end">
                  <span className={`inline-flex items-center px-6 py-3 rounded-lg text-[26px] font-mono-data font-bold tracking-wider ${comp.bg} ${comp.text}`}
                        data-testid={`pres-scorecard-compliance-${t.tech_name}`}>
                    {comp.label}
                  </span>
                </div>
              </div>

              {/* Per-item KPI compliance checklist — 5-across, all 10 items
                  always visible. Backend guarantees a full catalog even for
                  zero-job/standby techs (dashed placeholders), so the grid
                  shape stays uniform on the TV. */}
              {items.length > 0 && (
                <div className="border-t border-[#eef1f6] pt-4">
                  <div className="text-[14px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold mb-3">
                    KPI Compliance Checklist{t.is_standby ? " · standby" : ""}
                  </div>
                  <div className="grid grid-cols-5 gap-3" data-testid={`pres-scorecard-items-${t.tech_name}`}>
                    {items.map((it) => (
                      <KpiItemPill key={it.key} label={it.label} pct={it.pct} source={it.source} unanswered={it.unanswered}
                        testId={`pres-scorecard-item-${t.tech_name}-${it.key}`} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
